/**
 * SearchAndFilterBar - Reusable search and filter bar component
 *
 * Provides consistent layout for search input, filters, and action dropdowns
 *
 * @example
 * ```tsx
 * <SearchAndFilterBar
 *   searchValue={search}
 *   onSearchChange={setSearch}
 *   searchPlaceholder="Search by name..."
 *   filters={[
 *     {
 *       value: filterStatus,
 *       onChange: setFilterStatus,
 *       options: [
 *         { label: "All", value: "all" },
 *         { label: "Active", value: "active" },
 *       ],
 *       placeholder: "Filter by status",
 *     },
 *   ]}
 *   actions={[
 *     {
 *       placeholder: "Selection Actions",
 *       options: [
 *         { label: "Check All", value: "check-all" },
 *         { label: "Uncheck All", value: "uncheck-all" },
 *       ],
 *       onChange: (val) => {
 *         if (val === "check-all") handleCheckAll();
 *         else if (val === "uncheck-all") handleUncheckAll();
 *       },
 *       isDisabled: items.length === 0,
 *     },
 *   ]}
 * />
 * ```
 */

"use client";

import { Box, Flex } from "@chakra-ui/react";
import type { RefObject } from "react";
import { CustomSelect } from "@/components/shared/formElements/CustomSelect";
import { Search } from "@/components/ui";

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterConfig {
  /**
   * Playwright hook on the filter select trigger
   */
  "data-testid"?: string;
  /**
   * Stable key when duplicate filter configs appear in one bar
   */
  reactKey?: string;
  /**
   * Current filter value
   */
  value: string;
  /**
   * Callback when filter changes
   */
  onChange: (value: string) => void;
  /**
   * Filter options
   */
  options: FilterOption[];
  /**
   * Placeholder text
   */
  placeholder?: string;
  /**
   * Whether filter is disabled
   */
  isDisabled?: boolean;
  /**
   * Filter variant (default: "outline")
   */
  variant?: "outline" | "primary";
}

export interface ActionConfig {
  /**
   * Playwright hook on the actions select trigger
   */
  "data-testid"?: string;
  /**
   * Stable key when duplicate action configs appear in one bar
   */
  reactKey?: string;
  /**
   * Placeholder text
   */
  placeholder: string;
  /**
   * Action options
   */
  options: FilterOption[];
  /**
   * Callback when action is selected
   */
  onChange: (value: string) => void;
  /**
   * Whether action is disabled
   */
  isDisabled?: boolean;
  /**
   * Action variant (default: "primary")
   */
  variant?: "outline" | "primary";
}

export interface SearchAndFilterBarProps {
  /**
   * Wrapper for the bar (toolbar root)
   */
  "data-testid"?: string;
  /** `data-testid` on the search text field */
  searchDataTestId?: string;
  /**
   * Search input value
   */
  searchValue: string;
  /**
   * Callback when search changes
   */
  onSearchChange: (value: string) => void;
  /**
   * Search placeholder text
   */
  searchPlaceholder?: string;
  /**
   * Filter configurations
   */
  filters?: FilterConfig[];
  /**
   * Action dropdown configurations
   */
  actions?: ActionConfig[];
  /**
   * Ref for the actions container - used to detect when bulk actions scroll out of view.
   */
  actionsRef?: RefObject<HTMLDivElement | null>;
  /**
   * Custom content to display in the left section
   */
  leftContent?: React.ReactNode;
  /**
   * Custom content to display in the right section
   */
  rightContent?: React.ReactNode;
  /**
   * Position of search input on desktop (default: "left")
   * On mobile, search is always first (top)
   */
  searchPosition?: "left" | "right";
}

export const SearchAndFilterBar = ({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  filters = [],
  actions = [],
  actionsRef,
  leftContent,
  rightContent,
  searchPosition = "left",
  "data-testid": rootTestId,
  searchDataTestId,
}: SearchAndFilterBarProps) => {
  const searchComponent = (
    <Box flex="1" minW={{ base: "100%", md: "240px" }}>
      <Search
        value={searchValue}
        placeholder={searchPlaceholder}
        onChange={(e) => onSearchChange(e.target.value)}
        data-testid={searchDataTestId}
      />
    </Box>
  );

  const filtersComponent = filters.map((filter) => (
    <Box
      key={
        filter.reactKey ??
        `filter-${filter.placeholder ?? ""}-${filter.value}-${filter.options.map((o) => o.value).join(":")}`
      }
      minW={{ base: "100%", md: "200px" }}
    >
      <CustomSelect
        value={filter.value}
        onChange={filter.onChange}
        options={filter.options}
        placeholder={filter.placeholder}
        {...(filter["data-testid"]
          ? { "data-testid": filter["data-testid"] }
          : {})}
        formControlProps={{
          isDisabled: filter.isDisabled,
        }}
        variant={filter.variant || "outline"}
      />
    </Box>
  ));

  const actionsComponent = actions.map((action) => (
    <Box
      key={
        action.reactKey ??
        `action-${action.placeholder}-${action.options.map((o) => o.value).join(":")}`
      }
      w={{ base: "100%", md: "200px" }}
    >
      <CustomSelect
        value=""
        placeholder={action.placeholder}
        onChange={action.onChange}
        options={action.options}
        {...(action["data-testid"]
          ? { "data-testid": action["data-testid"] }
          : {})}
        formControlProps={{
          isDisabled: action.isDisabled,
        }}
        variant={action.variant || "primary"}
      />
    </Box>
  ));

  // When searchPosition is "right", filters go left and search goes right
  const isSearchRight = searchPosition === "right";

  return (
    <Flex
      {...(rootTestId ? { "data-testid": rootTestId } : {})}
      mb={{
        base: 0,
        md: 6,
      }}
      gap={3}
      wrap="wrap"
      justifyContent="space-between"
      direction={{
        base: "column",
        md: "row",
      }}
      alignItems={{
        base: "flex-start",
        md: "flex-end",
      }}
    >
      {/* Left section */}
      <Flex
        gap={3}
        wrap="wrap"
        w={{
          base: "100%",
          md: "max-content",
        }}
        direction={{
          base: "column",
          md: "row",
        }}
      >
        {leftContent || (
          <>
            {/* On mobile: search always first. On desktop: depends on searchPosition */}
            <Box
              display={{ base: "block", md: isSearchRight ? "none" : "block" }}
            >
              {searchComponent}
            </Box>
            {filtersComponent}
          </>
        )}
      </Flex>

      {/* Right section */}
      {(actions.length > 0 || rightContent || isSearchRight) && (
        <Flex
          ref={actionsRef}
          gap={3}
          wrap="wrap"
          w={{
            base: "100%",
            md: "max-content",
          }}
          direction={{
            base: "column",
            md: "row",
          }}
        >
          {rightContent || (
            <>
              {actionsComponent}
              {/* Search on right for desktop when searchPosition="right" */}
              {isSearchRight && (
                <Box display={{ base: "none", md: "block" }} minW="240px">
                  {searchComponent}
                </Box>
              )}
            </>
          )}
        </Flex>
      )}
    </Flex>
  );
};
